/**
 * Quick-test panel for a Mapping resolver.
 *
 * One typed input per declared mapping input, a Run button, a result
 * area that surfaces outputs / miss / error. Uses the same /resolve
 * endpoint the future ``call_mapping`` workflow node will hit, so
 * "works here = works there".
 *
 * Bypasses the ``enabled`` toggle on the backend by design — operators
 * test BEFORE flipping the mapping live. ``config_complete`` is still
 * required (the resolver would crash otherwise); the parent should
 * hide / disable this panel when the strategy config is incomplete.
 */
import { useMutation } from "@tanstack/react-query";
import { Loader2, Play } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  mappingsApi,
  type MappingField,
  type MappingResolveResponse,
  type MappingSummary,
} from "@/lib/api-mappings";

export interface MappingTestPanelProps {
  mapping: MappingSummary;
  /** True when the strategy config editor above has unsaved changes.
   *  /resolve reads the SAVED strategy_config, so a test now would
   *  not reflect what the user just edited — surface a warning so
   *  they know to Save first. */
  strategyDirty?: boolean;
}

export function MappingTestPanel({ mapping, strategyDirty }: MappingTestPanelProps) {
  // Raw string state for each input — we cast on submit per the
  // declared type. Mirrors the row-editor approach so the two surfaces
  // behave the same way ("string" types pass through verbatim, numbers
  // get parsed, booleans accept true/false/1/0).
  const [draft, setDraft] = React.useState<Record<string, string>>(() =>
    Object.fromEntries(mapping.inputs.map((f) => [f.name, ""])),
  );
  const [lastResult, setLastResult] = React.useState<MappingResolveResponse | null>(
    null,
  );

  const run = useMutation({
    mutationFn: () =>
      mappingsApi.resolve(
        mapping.id,
        castInputs(draft, mapping.inputs),
      ),
    onSuccess: (resp) => setLastResult(resp),
    onError: () => setLastResult(null),
  });

  function update(name: string, value: string) {
    setDraft((d) => ({ ...d, [name]: value }));
  }

  // Hide entirely when there's no resolver to test against — the
  // banner above the panel already directs the user to configure
  // the strategy.
  if (!mapping.config_complete) {
    return null;
  }

  return (
    <section className="space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Test resolve
        {!mapping.enabled && (
          <Badge tone="warning" className="ml-2 text-[10px]">
            ignores disabled state
          </Badge>
        )}
      </h2>
      <p className="text-[11px] text-muted-foreground">
        Try the mapping with sample inputs without enabling it or
        wiring it into a workflow. Same code path as the future
        <code className="mx-1 font-mono">call_mapping</code> node —
        what passes here works there.
      </p>

      {strategyDirty && (
        <Alert tone="warning">
          <strong>Strategy config has unsaved changes.</strong>
          <p className="mt-1 text-xs">
            Run test invokes the resolver against the{" "}
            <em>saved</em> configuration. Save the editor above to
            test the new values.
          </p>
        </Alert>
      )}

      <div className="rounded-lg border border-border/60 p-3">
        <div className="space-y-2">
          {mapping.inputs.map((f) => (
            <div
              key={f.name}
              className="grid grid-cols-[150px_1fr] items-center gap-3"
            >
              <Label htmlFor={`test-in-${f.name}`} className="text-xs">
                <span className="font-mono">{f.name}</span>
                <span className="ml-1 text-[10px] text-muted-foreground">
                  {f.type}
                </span>
              </Label>
              <Input
                id={`test-in-${f.name}`}
                value={draft[f.name] ?? ""}
                onChange={(e) => update(f.name, e.target.value)}
                placeholder={placeholderFor(f.type)}
                className="h-7 font-mono text-xs"
              />
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <Button
            size="sm"
            onClick={() => run.mutate()}
            disabled={run.isPending}
          >
            {run.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" strokeWidth={2} />
            )}
            Run test
          </Button>
          {run.isError && (
            <span className="text-xs text-danger">
              {(run.error as Error).message}
            </span>
          )}
        </div>

        {lastResult && !run.isError && (
          <ResultPanel result={lastResult} mapping={mapping} />
        )}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Result rendering
// ──────────────────────────────────────────────────────────────────────

function ResultPanel({
  result,
  mapping,
}: {
  result: MappingResolveResponse;
  mapping: MappingSummary;
}) {
  if (result.miss) {
    return (
      <Alert tone="info" className="mt-3">
        <strong>No match.</strong>
        <p className="mt-1 text-xs">
          The resolver ran but found nothing for these inputs. In a
          workflow, the <code className="font-mono">call_mapping</code>{" "}
          node's <code className="font-mono">on_miss</code> setting
          decides the fallback (fail / pass-through / default).
        </p>
      </Alert>
    );
  }
  const outputs = result.outputs ?? {};
  return (
    <div className="mt-3 rounded border border-success/40 bg-success/5 p-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-success">
        Hit — outputs
      </div>
      <table className="w-full text-sm">
        <tbody>
          {mapping.outputs.map((f) => {
            const v = outputs[f.name];
            return (
              <tr key={f.name} className="border-t border-border/40 first:border-t-0">
                <td className="w-1/3 py-1 font-mono text-xs">
                  {f.name}
                  <span className="ml-1 text-[10px] text-muted-foreground">
                    {f.type}
                  </span>
                </td>
                <td className="py-1 font-mono text-xs">
                  {formatValue(v)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Cell helpers — kept local to avoid pulling MappingDetailPage helpers
// (they assume row context; we have inputs/outputs only).
// ──────────────────────────────────────────────────────────────────────

function placeholderFor(t: MappingField["type"]): string {
  if (t === "boolean") return "true / false";
  if (t === "integer") return "42";
  if (t === "number") return "3.14";
  return "value";
}

function formatValue(v: unknown): React.ReactNode {
  if (v === null || v === undefined) {
    return <span className="text-muted-foreground italic">null</span>;
  }
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function castInputs(
  raw: Record<string, string>,
  fields: MappingField[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const v = raw[f.name];
    if (v === undefined || v === "") {
      // Empty on a required field → let the server complain with a
      // readable message (we don't pre-validate here).
      continue;
    }
    out[f.name] = castOne(v, f.type);
  }
  return out;
}

function castOne(v: string, t: MappingField["type"]): unknown {
  switch (t) {
    case "string":
      return v;
    case "integer":
      return parseInt(v, 10);
    case "number":
      return Number(v);
    case "boolean":
      return v.toLowerCase() === "true" || v === "1";
  }
}
